import { SelectOption } from 'global';
import React, { ReactNode } from 'react';
import { useQuery, useLazyQuery } from '@apollo/client';
import { useNotifications } from 'modules/notification';
import { FinanceFormValues } from 'modules/desking/shared';
import { CreditTiersResult } from '@trnsact/trnsact-shared-types';
import { removeGraphQLMEtaFields } from 'utils/removeGraphQLMEtaFields';
import {
  FinanceProgram,
  GET_FINANCE_PROGRAMS_BY_ID,
  GET_FINANCE_PROGRAMS_OPTIONS_LIST_BY_ACCOUNT,
} from 'modules/financeProgram';
import { lenderDeskingPanelMapper } from '../mappers';

interface Props {
  accountId: string;
  children: (renderProps: {
    loading: { isFPOptionsLoading: boolean };
    financeProgramOptions: SelectOption[];
    actions: {
      handleFetchFinanceProgram: (
        programId: string,
        scoreCardResult?: CreditTiersResult
      ) => Promise<Partial<FinanceFormValues['financeQuote']>>;
    };
  }) => ReactNode;
}

export const LenderDeskingPanelApiContainer = ({ accountId, children }: Props) => {
  const { showNotification } = useNotifications();

  const [onFetchFPById] = useLazyQuery<{ financeProgram: FinanceProgram }>(GET_FINANCE_PROGRAMS_BY_ID);

  const { data, loading: isFPOptionsLoading } = useQuery<{ options: SelectOption[] }, { accountId: string }>(
    GET_FINANCE_PROGRAMS_OPTIONS_LIST_BY_ACCOUNT,
    {
      variables: { accountId },
      onError() {
        showNotification('Something went wrong with option list of Finance Programs', { type: 'error' });
      },
    }
  );

  const handleFetchFinanceProgram = async (programId: string, scoreCardResult?: CreditTiersResult) => {
    const { data } = await onFetchFPById({ variables: { financeProgramId: programId } });
    return lenderDeskingPanelMapper.processProgramToForm(data?.financeProgram ?? null, scoreCardResult);
  };

  return (
    <>
      {children({
        loading: { isFPOptionsLoading },
        actions: { handleFetchFinanceProgram },
        financeProgramOptions: data?.options?.map(removeGraphQLMEtaFields) ?? [],
      })}
    </>
  );
};
